import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Building2, CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money, openCheckout } from "@/lib/api";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";

/** The corporate customer's own statement: every trip that month, one total, pay by card. */
export default function StatementView() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");

  const load = useCallback(
    () => api.get(`/public/statements/${token}`).then(({ data }) => setData(data))
      .catch((e) => setError(errMsg(e))),
    [token]
  );
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!params.get("paid")) return undefined;
    const timer = setTimeout(load, 4000);
    return () => clearTimeout(timer);
  }, [params, load]);

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
        {error ? <p data-testid="statement-error" className="text-center text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  const { statement: s, company: c } = data;
  const pay = async () => {
    setBusy(true); setError(""); setCheckoutUrl("");
    try {
      const { data: res } = await api.post(`/public/statements/${token}/checkout`);
      if (!openCheckout(res.checkout_url, setCheckoutUrl)) setCheckoutUrl(res.checkout_url);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="statement-page" className="min-h-screen bg-ink-900 px-4 py-10 text-slate-200">
      <div className="mx-auto max-w-3xl space-y-5 rise">
        <div className="rounded-2xl border border-ink-600 bg-ink-800 p-6"
          style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
              <h1 className="mt-2 flex items-center gap-2 text-2xl font-bold text-slate-50">
                <Building2 className="h-5 w-5 text-gold" /> Statement #{s.number}
              </h1>
              <p className="mt-1 text-sm text-slate-400">
                {s.account_name} · {s.month} · {s.trips} trip{s.trips === 1 ? "" : "s"}
              </p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Total</p>
              <p data-testid="statement-total" className="font-display text-3xl font-bold text-slate-50">{money(s.total)}</p>
              <p className="mt-1 text-xs text-slate-400">{s.terms_label} · due {s.due_date}</p>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-ink-600 bg-ink-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-ink-900/60">
              <tr className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Trip</th>
                <th className="hidden px-4 py-3 sm:table-cell">Route</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map((line) => (
                <tr key={line.manifest_id} data-testid={`statement-line-${line.number}`} className="border-t border-ink-600">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300">{line.trip_date} {line.pickup_time}</td>
                  <td className="px-4 py-3 text-slate-100">#{line.number} · {line.passenger}</td>
                  <td className="hidden max-w-xs truncate px-4 py-3 text-xs text-slate-500 sm:table-cell">{line.route}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-slate-100">{money(line.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {s.status === "paid" ? (
          <div data-testid="statement-paid" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center">
            <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-400" />
            <p className="mt-3 text-lg font-semibold text-slate-50">{money(s.amount_paid || s.total)} settled — thank you.</p>
            <p className="mt-1 text-sm text-emerald-300">
              Paid {s.payment_method ? `by ${s.payment_method}` : ""}{s.paid_at ? ` on ${s.paid_at.slice(0, 10)}` : ""}.
            </p>
          </div>
        ) : (
          <div className="space-y-4 rounded-2xl border border-ink-600 bg-ink-800 p-6">
            {data.sandbox && <SandboxNotice testId="statement-sandbox" />}
            {error && <p data-testid="statement-pay-error" className="text-sm text-red-400">{error}</p>}
            {data.payments_ready ? (
              <>
                <Button data-testid="statement-pay" disabled={busy} onClick={pay}
                  className="h-16 w-full bg-gold text-base font-semibold text-ink-900 hover:bg-gold-light">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                    : <><CreditCard className="mr-2 h-5 w-5" /> Pay {money(s.total)} by card</>}
                </Button>
                {checkoutUrl && (
                  <a data-testid="statement-checkout-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                    className="block rounded-xl border border-gold/40 bg-gold/10 p-4 text-center text-sm font-semibold text-gold">
                    Open the secure card form →
                  </a>
                )}
              </>
            ) : (
              <p data-testid="statement-no-card" className="text-sm text-slate-400">
                {c.name} is not set up for online card payments. Please remit by check or ACH, or
                call {c.phone || "the office"}.
              </p>
            )}
            <p className="text-center text-xs text-slate-500">
              Questions on this statement? Reply to the email it came from or call {c.phone || c.email}.
            </p>
          </div>
        )}

        {c.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </div>
    </div>
  );
}
